import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, SendIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
const WEB3FORMS_ACCESS_KEY = '9f6d2e40-eb5c-4438-9a46-6e175eae72f4';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    inquiryType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `[AU Contact] ${formData.inquiryType || 'General'} — ${formData.subject}`,
        from_name: formData.name,
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        inquiry_type: formData.inquiryType,
        message_subject: formData.subject,
        message: formData.message,
        botcheck: ''
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          organization: '',
          subject: '',
          inquiryType: '',
          message: ''
        });
      } else {
        setError(data.message || 'Something went wrong. Please try again or email us directly.');
      }
    } catch (err: any) {
      setError(err?.message || 'Could not send your message. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const contactInfo = [{
    icon: MapPinIcon,
    title: 'Office Address',
    lines: ['African Union Commission', '10 Carlton House Terrace', 'London SW1Y 5AA', 'United Kingdom']
  }, {
    icon: PhoneIcon,
    title: 'Phone',
    lines: ['+44 (0) 20 4571 0123 (Procurement)', '+44 (0) 20 4571 1964 (General)']
  }, {
    icon: MailIcon,
    title: 'Email',
    lines: ['info@aucommission.org', 'procurement@aucommission.org']
  }, {
    icon: ClockIcon,
    title: 'Office Hours',
    lines: ['Monday – Friday: 09:00 – 17:30 GMT', 'Saturday – Sunday: Closed', 'Public Holidays: Closed']
  }];
  return <div data-id="element-674" data-mp="2rewo9pep47xn">
      <PageHero title="Contact Us" subtitle="Get in touch with the African Union Commission" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Contact'
    }]} data-id="element-675" data-mp="2re4sadh6s86u" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-676" data-mp="2mfjuekpeq0lk">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-677" data-mp="2mg397sjrg8vd">
          {contactInfo.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} data-id="element-678" data-mp="3f367qkofsjlm" data-mpi={index}>
                <Card className="h-full" data-id="element-679" data-mp="1tr4nymkccqlu">
                  <div className="w-12 h-12 bg-au-green-light rounded-lg flex items-center justify-center mb-4" data-id="element-680" data-mp="1toryloc97dyr">
                    <Icon size={24} className="text-au-green" data-id="element-681" data-mp="29zu6xy04qk3h" />
                  </div>
                  <h3 className="font-bold text-charcoal mb-3" data-id="element-682" data-mp="2557stn2nydfp">{item.title}</h3>
                  <div className="space-y-1" data-id="element-683" data-mp="208r16iw99gxv">
                    {item.lines.map((line, i) => <p key={i} className="text-sm text-gray-600" data-id="element-684" data-mp="1vapmea5v43s7" data-mpi={i}>
                        {line}
                      </p>)}
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="grid lg:grid-cols-2 gap-8" data-id="element-685" data-mp="2iy628dbxy629">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-686" data-mp="2iww4xqcwx1wo">
            <Card data-id="element-687" data-mp="13h5ove7zohs7">
              <h2 className="text-2xl font-serif text-charcoal mb-2" data-id="element-688" data-mp="13gnj9qzfsr7m">
                Send Us a Message
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-689" data-mp="3f3hbxf6utetq"></div>

              {submitted ? <div className="py-12 text-center" data-id="element-690" data-mp="3j4j97qh7ngt2">
                  <div className="w-16 h-16 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-691" data-mp="3o4xhn9mq4srm">
                    <CheckCircleIcon size={32} className="text-au-green" data-id="element-692" data-mp="3sy046xt331yb" />
                  </div>
                  <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-693" data-mp="1q4fzx439kj5">
                    Message Sent
                  </h3>
                  <p className="text-gray-600" data-id="element-694" data-mp="ar2lyky3824d">
                    Thank you for reaching out. We'll respond within 2 business
                    days.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-695" data-mp="2cip6q4rdz0se">
                  {/* Honeypot anti-spam field — hidden from real users */}
                  <input type="checkbox" name="botcheck" className="hidden" style={{
                display: 'none'
              }} tabIndex={-1} aria-hidden="true" data-id="element-696" data-mp="2hi1zt78up43f" />
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-697" data-mp="3tr1ajhir1qkc">
                    <div data-id="element-698" data-mp="3ot9j0om0o6go">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-699" data-mp="3osq31dfxfs5a">
                        Full Name <span className="text-au-red" data-id="element-700" data-mp="7k2yabo686y8">*</span>
                      </label>
                      <input type="text" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Your full name" data-id="element-701" data-mp="2mkpqme2ftqr" />
                    </div>
                    <div data-id="element-702" data-mp="19w4fk1rtg0ij">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-703" data-mp="v16mt7sr990x">
                        Email <span className="text-au-red" data-id="element-704" data-mp="10017kfaa97no">*</span>
                      </label>
                      <input type="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="you@email.com" data-id="element-705" data-mp="m8mlu0ozqscx" />
                    </div>
                  </div>

                  <div data-id="element-706" data-mp="nptlb4cpo96g">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-707" data-mp="nqcvu0017vag">
                      Organization
                    </label>
                    <input type="text" value={formData.organization} onChange={e => setFormData({
                  ...formData,
                  organization: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Company or organization" data-id="element-708" data-mp="dsf95gxtm6jy" />
                  </div>

                  <div data-id="element-709" data-mp="3h9e62rmd1pfu">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-710" data-mp="3h9xbx9yqkndi">
                      Inquiry Type <span className="text-au-red" data-id="element-711" data-mp="3d7ajb6u8qyf9">*</span>
                    </label>
                    <select required value={formData.inquiryType} onChange={e => setFormData({
                  ...formData,
                  inquiryType: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-712" data-mp="1sq1kk9bnj77y">
                      <option value="" data-id="element-713" data-mp="1e0lyh6n2fpyl">Select inquiry type</option>
                      <option value="procurement" data-id="element-714" data-mp="1e158802kb81j">Procurement & Bids</option>
                      <option value="partnership" data-id="element-715" data-mp="1iz6oyecimv9u">
                        Partnership Opportunities
                      </option>
                      <option value="projects" data-id="element-716" data-mp="1nshwyit2di3c">Projects & Programmes</option>
                      <option value="media" data-id="element-717" data-mp="qarjaif9xpb6">Media & Press</option>
                      <option value="general" data-id="element-718" data-mp="v9m1pj9bxbur">General Inquiry</option>
                    </select>
                  </div>

                  <div data-id="element-719" data-mp="owsuwcvxddbl">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-720" data-mp="ozoojp0qpfu8">
                      Subject <span className="text-au-red" data-id="element-721" data-mp="tuudkwpfvamj">*</span>
                    </label>
                    <input type="text" required value={formData.subject} onChange={e => setFormData({
                  ...formData,
                  subject: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Brief subject line" data-id="element-722" data-mp="ywb9edt2g6w9" />
                  </div>

                  <div data-id="element-723" data-mp="3jlhehxe9wam0">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-724" data-mp="3jk6co34e1cxj">
                      Message <span className="text-au-red" data-id="element-725" data-mp="3oizeuze85hie">*</span>
                    </label>
                    <textarea required rows={6} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green resize-none" placeholder="How can we help you?" data-id="element-726" data-mp="25p6c81id0dwx" />
                  </div>

                  {error && <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-727" data-mp="1908hukeiui0a">
                      <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-728" data-mp="13zap5dvvy5kj" />
                      <span data-id="element-729" data-mp="pdwvdhen3a7w">{error}</span>
                    </div>}

                  <Button type="submit" variant="primary" disabled={submitting} className="flex items-center gap-2 disabled:opacity-60" data-id="element-730" data-mp="pg9kqfmq8muz">
                    <SendIcon size={18} data-id="element-731" data-mp="ke24hduv6kmu" />
                    {submitting ? 'Sending…' : 'Send Message'}
                  </Button>
                </form>}
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} className="space-y-6" data-id="element-732" data-mp="1d2g23si1jexv">
            <Card className="p-0 overflow-hidden" data-id="element-733" data-mp="1r2h2z7yb2mdc">
              <div className="aspect-square bg-au-green-light relative" data-id="element-734" data-mp="1vtz6a2nswz1h">
                <iframe title="African Union Commission Office Location" src="https://www.openstreetmap.org/export/embed.html?bbox=-0.1340%2C51.5050%2C-0.1280%2C51.5080&layer=mapnik&marker=51.5065%2C-0.1310" className="w-full h-full border-0" loading="lazy" data-id="element-735" data-mp="1vvabm6wwaeup"></iframe>
              </div>
              <div className="p-6" data-id="element-736" data-mp="3lcfhhcjhadls">
                <div className="flex items-start gap-3" data-id="element-737" data-mp="3ldgvnpvsjg7q">
                  <MapPinIcon size={20} className="text-au-green flex-shrink-0 mt-1" data-id="element-738" data-mp="2xmbff9lxulww" />
                  <div data-id="element-739" data-mp="2sotdw4a938yx">
                    <h3 className="font-bold text-charcoal mb-1" data-id="element-740" data-mp="37k94u0fjaor3">
                      Visit Our Office
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-741" data-mp="32jtt1dawhpb8">
                      10 Carlton House Terrace, London SW1Y 5AA
                      <br data-id="element-742" data-mp="mrnus6udasdn" />
                      Nearest Tube: Charing Cross / Piccadilly Circus
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-au-green text-white border-au-green" data-id="element-743" data-mp="2nkraoe61765t">
              <h3 className="text-xl font-serif mb-2" data-id="element-744" data-mp="2sikg34zifw51">Procurement Inquiries</h3>
              <div className="w-12 h-1 bg-au-gold mb-4" data-id="element-745" data-mp="2wj36qvpo2w5n"></div>
              <p className="text-au-green-light mb-4 text-sm" data-id="element-746" data-mp="2wlpccpszz1l8">
                For all bid, tender, and contract-related inquiries, please
                contact our procurement team directly for the fastest response.
              </p>
              <div className="space-y-2 text-sm" data-id="element-747" data-mp="3bftycit3n3kp">
                <div className="flex items-center gap-2" data-id="element-748" data-mp="3beiy2syv4dyc">
                  <MailIcon size={16} data-id="element-749" data-mp="3g8etbnnqrmpt" />
                  <a href="mailto:procurement@aucommission.org" className="hover:text-au-gold transition-colors" data-id="element-750" data-mp="3g9pt7ce2gabn">
                    procurement@aucommission.org
                  </a>
                </div>
                <div className="flex items-center gap-2" data-id="element-751" data-mp="3u7wkg9oifc2c">
                  <PhoneIcon size={16} data-id="element-752" data-mp="85e4qb9jktfu" />
                  <span data-id="element-753" data-mp="86gv8i2yctv8">+44 (0) 20 4571 0123 (Procurement)</span>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>;
}